(() => {
  if (window.__fumiCard2Loaded) return;
  window.__fumiCard2Loaded = true;

class Card2Product {
  constructor(root) {
    this.root = root;
    this.variants = this.parseJson('[data-card2-variants]') || [];
    this.selectedOptions = {};
    this.initializeSelectedOptions();
    this.bindMedia();
    this.bindOptions();
    this.bindForm();
    this.refreshVariant();
    this.refreshSizeAvailability();
  }

  parseJson(selector) {
    const node = this.root.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  }

  initializeSelectedOptions() {
    this.root.querySelectorAll('[data-card2-option-value].is-selected').forEach((btn) => {
      const position = Number(btn.dataset.optionPosition);
      const value = btn.dataset.optionValue;
      if (position && value) this.selectedOptions[position] = value;
    });

    if (Object.keys(this.selectedOptions).length === 0) {
      const variant = this.getDefaultVariant();
      if (variant) {
        this.selectedOptions = {
          1: variant.option1 || null,
          2: variant.option2 || null,
          3: variant.option3 || null,
        };
      }
    }
  }

  getDefaultVariant() {
    const variantId = Number(this.root.querySelector('[data-card2-variant-id]')?.value);
    return (
      this.variants.find((variant) => variant.id === variantId) ||
      this.variants.find((variant) => variant.available) ||
      this.variants[0] ||
      null
    );
  }

  getSelectedVariant() {
    const opts = this.selectedOptions || {};
    const matched = this.variants.find(
      (variant) =>
        (variant.option1 || null) === (opts[1] || null) &&
        (variant.option2 || null) === (opts[2] || null) &&
        (variant.option3 || null) === (opts[3] || null)
    );

    return matched || this.getDefaultVariant();
  }

  bindMedia() {
    this.slides = [...this.root.querySelectorAll('[data-card2-media-slide]')];
    if (this.slides.length <= 1) return;

    this.mediaIndex = 0;
    this.root.querySelector('[data-card2-media-prev]')?.addEventListener('click', () => this.showMedia(this.mediaIndex - 1));
    this.root.querySelector('[data-card2-media-next]')?.addEventListener('click', () => this.showMedia(this.mediaIndex + 1));
    this.root.querySelectorAll('[data-card2-media-dot]').forEach((dot) => {
      dot.addEventListener('click', () => this.showMedia(Number(dot.dataset.card2MediaDot)));
    });
  }

  showMedia(index) {
    if (!this.slides.length) return;
    this.mediaIndex = (index + this.slides.length) % this.slides.length;

    this.slides.forEach((slide, i) => {
      const active = i === this.mediaIndex;
      slide.classList.toggle('is-active', active);
      slide.hidden = !active;
    });

    this.root.querySelectorAll('[data-card2-media-dot]').forEach((dot, i) => {
      dot.classList.toggle('is-active', i === this.mediaIndex);
    });
  }

  bindOptions() {
    this.root.querySelectorAll('[data-card2-option-value]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const position = Number(btn.dataset.optionPosition);
        const value = btn.dataset.optionValue;
        if (!position || !value) return;

        this.selectedOptions[position] = value;
        this.root.querySelectorAll(`[data-card2-option-value][data-option-position="${position}"]`).forEach((el) => {
          el.classList.toggle('is-selected', el === btn);
        });
        this.refreshVariant();
        this.refreshSizeAvailability();
      });
    });
  }

  formatMoney(cents) {
    const format =
      this.root.closest('[data-fumi-yml], [data-fumi-mp]')?.dataset.moneyFormat ||
      window.theme?.moneyFormat ||
      '${{amount}}';

    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, format);
    }

    return `$${(cents / 100).toFixed(2)}`;
  }

  refreshVariant() {
    const variant = this.getSelectedVariant();
    if (!variant) return;

    const variantInput = this.root.querySelector('[data-card2-variant-id]');
    const addBtn = this.root.querySelector('[data-card2-add]');
    const priceEl = this.root.querySelector('[data-card2-price]');
    const compareEl = this.root.querySelector('[data-card2-compare]');

    if (variantInput) variantInput.value = variant.id;
    if (addBtn) addBtn.disabled = !variant.available;

    if (priceEl) priceEl.textContent = this.formatMoney(variant.price);

    if (compareEl) {
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        compareEl.textContent = this.formatMoney(variant.compare_at_price);
        compareEl.hidden = false;
      } else {
        compareEl.hidden = true;
      }
    }
  }

  refreshSizeAvailability() {
    const opts = this.selectedOptions || {};
    this.root.querySelectorAll('[data-card2-option-value]').forEach((btn) => {
      const position = Number(btn.dataset.optionPosition);
      const value = btn.dataset.optionValue;
      if (!position || !value) return;

      const match = this.variants.find((variant) => {
        const checks = [
          [1, variant.option1],
          [2, variant.option2],
          [3, variant.option3],
        ];
        return checks.every(([pos, optVal]) => (pos === position ? optVal === value : optVal === (opts[pos] || null)));
      });

      const isSize = btn.classList.contains('bl-card__size');
      if (!isSize) return;

      const soldOut = !match || !match.available;
      btn.classList.toggle('is-sold-out', soldOut);
      btn.disabled = soldOut;
    });
  }

  bindForm() {
    const form = this.root.querySelector('[data-card2-form]');
    if (!form) return;

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const addBtn = form.querySelector('[data-card2-add]');
      if (!addBtn || addBtn.disabled) return;

      addBtn.disabled = true;
      const formData = new FormData(form);

      try {
        if (typeof window.fumiAddToCart === 'function') {
          await window.fumiAddToCart({ formData, triggerEl: addBtn });
        } else {
          const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

          if (cart?.getSectionsToRender) {
            formData.append(
              'sections',
              cart.getSectionsToRender().map((section) => section.id).join(',')
            );
            formData.append('sections_url', window.location.pathname);
            cart.setActiveElement?.(document.activeElement);
          }

          const config =
            typeof fetchConfig === 'function'
              ? fetchConfig('javascript')
              : { method: 'POST', headers: { Accept: 'application/javascript' } };
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          config.body = formData;

          const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}`, config);
          const data = await response.json();
          if (data.status) throw new Error(data.description || data.message);

          const didRenderDrawer = !!(cart?.renderContents && data.sections);
          if (didRenderDrawer) {
            cart.renderContents(data);
          } else if (window.publish && window.PUB_SUB_EVENTS) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'fumi-card2',
              productVariantId: formData.get('id'),
              cartData: data,
            });
          }
        }
      } catch (error) {
        console.error(error);
      } finally {
        const variant = this.getSelectedVariant();
        if (addBtn) addBtn.disabled = !variant?.available;
      }
    });
  }
}

function initFumiYouMayAlsoLike(scope = document) {
  const cards = [];
  if (scope instanceof Element && scope.matches('[data-card2]')) {
    cards.push(scope);
  }
  cards.push(...scope.querySelectorAll('[data-card2]'));

  cards.forEach((card) => {
    if (card.dataset.card2Init === 'true') return;
    card.dataset.card2Init = 'true';
    new Card2Product(card);
  });
}

function watchCard2Products() {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (!node.matches('[data-card2]') && !node.querySelector('[data-card2]')) return;
        initFumiYouMayAlsoLike(node);
      });
    });
  });

  observer.observe(document.body, { childList: true, subtree: true });
}

function watchRecommendations(scope = document) {
  scope.querySelectorAll('product-recommendations').forEach((node) => {
    if (node.dataset.fumiYmlWatch === 'true') return;
    node.dataset.fumiYmlWatch = 'true';

    let debounceTimer;
    const observer = new MutationObserver(() => {
      clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        initFumiYouMayAlsoLike(node);

        const hasCards = node.querySelector('[data-card2]');
        const pending = node.querySelector('[data-card2]:not([data-card2-init])');
        if (hasCards && !pending) observer.disconnect();
      }, 100);
    });

    observer.observe(node, { childList: true, subtree: true });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initFumiYouMayAlsoLike();
  watchCard2Products();
  watchRecommendations();
});

if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    initFumiYouMayAlsoLike(event.target);
    watchRecommendations(event.target);
  });
}
})();
